// All comments in English only
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  Area,
} from "recharts";
import { Info } from "lucide-react";

interface Point {
  usage_month: string;
  risk_score_final: number;
  churn_probability_company: number;
}

interface Props {
  title?: string;
  data: Point[];
}

export default function RiskHistoryChart({ title = "Company Risk History", data }: Props) {
  const rows = (data || []).map((d) => ({
    month: d.usage_month?.slice(0, 7),
    risk: d.risk_score_final,
    churn: Math.round((d.churn_probability_company ?? 0) * 100),
  }));

  // Compute simple trend info for explanation
  const last = rows[rows.length - 1];
  const first = rows[0];
  const trendUp = last && first ? last.risk > first.risk : false;
  const churnUp = last && first ? last.churn > first.churn : false;

  return (
    <Card className="w-full flex flex-col md:flex-row">
      {/* Chart Section */}
      <div className="flex-1 p-4">
        <CardHeader className="pb-2">
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ left: 8, right: 8, top: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" minTickGap={24} />
              {/* left axis for risk */}
              <YAxis yAxisId="left" domain={[0, "auto"]} />
              {/* right axis for churn */}
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
              <Tooltip />
              <Legend />

              {/* Highlight 0~100 churn band area for better comparison */}
              <Area
                yAxisId="right"
                type="monotone"
                dataKey="churn"
                name="Churn %"
                fill="#fde68a" // amber-200
                stroke="none"
                opacity={0.3}
              />
              {/* Reference line at 50% churn */}
              <ReferenceLine y={50} yAxisId="right" stroke="#facc15" strokeDasharray="4 4" />

              {/* Risk Score = orange line */}
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="risk"
                name="Risk Score"
                dot={false}
                stroke="#f97316" // orange-500
                strokeWidth={2.5}
                activeDot={{ r: 5 }}
              />
              {/* Churn % = red line */}
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="churn"
                name="Churn %"
                dot={false}
                stroke="#ef4444" // red-500
                strokeWidth={2.5}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </div>

      {/* Explanation Section */}
      <div className="md:w-64 border-t md:border-t-0 md:border-l border-border p-4 flex flex-col gap-2 bg-muted/30">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Info className="h-4 w-4 text-muted-foreground" />
          How to read
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          <strong>Risk Score</strong> (orange) shows the company’s composite risk trend.
          <br />
          <strong>Churn %</strong> (red) shows predicted probability of contract churn.
          <br />
          Yellow band highlights churn level zones.
        </p>

        <div className="mt-2 text-sm">
          <strong>Latest Summary</strong>
          <ul className="list-disc pl-4 mt-1 text-muted-foreground">
            <li>
              Risk score is{" "}
              <span className={trendUp ? "text-red-500" : "text-green-600"}>
                {trendUp ? "increasing" : "decreasing"}
              </span>{" "}
              over time.
            </li>
            <li>
              Churn probability is{" "}
              <span className={churnUp ? "text-red-500" : "text-green-600"}>
                {churnUp ? "rising" : "falling"}
              </span>{" "}
              in recent months.
            </li>
            <li>
              Compare peaks to identify months with sudden behavior shifts.
            </li>
          </ul>
        </div>
      </div>
    </Card>
  );
}
